import { LoginForm } from "@/components/Complex/LoginForm"
import { AuthLayout } from "@/components/Primitives/AuthLayout"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useState } from "react"
import { login } from "@/lib/api/auth"
import { saveToken } from "@/hooks/useLocalStorage"
import { setToken } from "@/lib/api/client"
import axios from "axios"
import { useConfig } from "@/hooks/useConfig"
import { z } from "zod/v4"

export function LoginPage() {
  const navigate = useNavigate()
  const { t } = useConfig()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string
    password?: string
  }>({})
  const [searchParams] = useSearchParams()
  const nextPathName = searchParams.get("next")

  const showError = (error: unknown) => {
    console.error("Login failed:", error)
    setError(t("config_login_error_generic"))
  }

  const handleSubmit = async () => {
    try {
      setError("")
      setFieldErrors({})

      const emailSchema = z.email(t("config_login_email_error"))
      const passwordSchema = z.string().min(1, t("config_login_password_error"))

      const emailResult = emailSchema.safeParse(email)
      const passwordResult = passwordSchema.safeParse(password)

      const newFieldErrors: {
        email?: string
        password?: string
      } = {}

      if (!emailResult.success) {
        const issues = emailResult.error.issues
        newFieldErrors.email = issues[0]?.message
      }

      if (!passwordResult.success) {
        const issues = passwordResult.error.issues
        newFieldErrors.password = issues[0]?.message
      }

      if (Object.keys(newFieldErrors).length > 0) {
        setFieldErrors(newFieldErrors)
        return
      }

      setIsSubmitting(true)
      const token = await login(email, password)
      saveToken(token)
      setToken(token)
      if (nextPathName) {
        navigate(nextPathName)
      } else {
        navigate("/quests")
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status
        if (status === 400) {
          setError(t("config_login_error_credentials"))
        } else {
          showError(error)
        }
      } else {
        showError(error)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthLayout
      description={t("config_login_layout_description")}
      imageUrl="/images/open-box.webp"
    >
      <LoginForm
        email={email}
        password={password}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        onSubmit={handleSubmit}
        nextPathName={nextPathName || "/quests"}
        fieldErrors={fieldErrors}
        isSubmitting={isSubmitting}
        error={error}
      />
    </AuthLayout>
  )
}
